import React from 'react';
import useInputHelper from './inputHelper';
import { useUiTheme } from 'framework/uiTheme';
import { CommonInputLayoutProps, BaseChangeEvent, DefaultInputPropTypes } from './types';

export type NumberInputLayoutType = ReactComponent<CommonInputLayoutProps<BaseChangeEvent, number>>;

const DECIMAL_KEYS = ['.', ',', 'e', 'E'];

const NumberInput = ({
  isVisible = true,
  integerOnly = false,
  ...otherProps
}: DefaultInputPropTypes<number> & { integerOnly?: boolean }) => {
  const props = {
    isVisible,
    ...otherProps,
  };

  const inputHelper = useInputHelper(props);
  const { NumberInputLayout } = useUiTheme<NumberInputLayoutType>();

  const getMin = (): number | undefined => {
    const htmlInput = inputHelper.getSlotProps()?.htmlInput;

    if (!htmlInput) return undefined;

    const htmlInputProps = typeof htmlInput === 'function' ? htmlInput({} as any) : htmlInput;

    return typeof htmlInputProps.min === 'number' ? Number(htmlInputProps.min) : undefined;
  };

  const getKeyDown = (e: React.KeyboardEvent): void => {
    const min = getMin();

    if (min !== undefined && min >= 0 && ['-', '+'].includes(e.key)) {
      e.preventDefault();
    }

    if (integerOnly && DECIMAL_KEYS.includes(e.key)) {
      e.preventDefault();
    }
  };

  const onChangeCallback = inputHelper.getOnChangeCallback();

  const handleChange = (event: any): void => {
    const rawValue = event?.target?.value;

    onChangeCallback(integerOnly && typeof rawValue === 'string' ? rawValue.replace(/[.,e].*$/i, '') : event);
  };

  return (
    <NumberInputLayout
      isVisible={inputHelper.isVisible()}
      label={inputHelper.getLabel()}
      source={inputHelper.getSource()}
      placeholder={inputHelper.getPlaceholder()}
      onChangeCallback={handleChange}
      onBlurCallback={inputHelper.getValidationCallback()}
      onFocusCallback={inputHelper.getClearValidationCallback()}
      value={inputHelper.getValue()}
      errorMessages={inputHelper.getErrorMessages()}
      isRequired={inputHelper.getIsRequired()}
      labelHint={inputHelper.getLabelHint()}
      helpText={inputHelper.getHelpText()}
      disabled={inputHelper.getIsDisabled()}
      tooltip={inputHelper.getTooltip()}
      className={inputHelper.getClassName()}
      sx={inputHelper.getSX()}
      onKeyDown={getKeyDown}
      slotProps={inputHelper.getSlotProps()}
    />
  );
};

export default NumberInput;
